import { useCallback, useMemo } from "react";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { VStack } from "@design-system/VStack/VStack";
import { HStack } from "@design-system/HStack/HStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useRemindOrgIntegrationConnectionsMutation } from "@api/organizationIntegration.api";
import { useUser } from "@hooks/useUser";
import { useIntegrationRowActions } from "./IntegrationRowActionsContext";
import { reconnectReminderPreview } from "./reconnectReminderPreview";
import styles from "./ReconnectReminderPreviewModal.module.scss";

interface ReconnectReminderPreviewModalProps {
    item: OrganizationIntegrationAccountItem;
    show: boolean;
    onClose: () => void;
}

/**
 * Shows the client exactly what they will receive, and asks the firm member to
 * approve it before anything is sent.
 *
 * Read-only: the body is a mirror of the server-side template, not an editable
 * field, so there is nothing to submit but a confirmation.
 */
export const ReconnectReminderPreviewModal = ({
    item,
    show,
    onClose,
}: ReconnectReminderPreviewModalProps) => {
    const { organizationId, markConnectionReminded } =
        useIntegrationRowActions();
    const currentUser = useUser();
    const { toast, error: toastError } = useToaster();
    const remindConnections =
        useRemindOrgIntegrationConnectionsMutation(organizationId);

    const preview = useMemo(
        () =>
            reconnectReminderPreview({
                item,
                requesterName:
                    currentUser?.name ?? currentUser?.email ?? "A teammate",
            }),
        [item, currentUser?.name, currentUser?.email],
    );

    /**
     * The endpoint answers 200 even when nothing was sent — an ineligible row or
     * the 24h cooldown comes back in `failed`. So success is `sent`, not status,
     * and a failure keeps the dialog open with the server's own reason.
     */
    const handleSend = useCallback(async () => {
        const connectionId = item.connection.id;

        let result;
        try {
            result = await remindConnections.mutateAsync([connectionId]);
        } catch {
            // Already toasted by the shared interceptor.
            return;
        }

        const failure = result.failed[0];
        if (failure) {
            toastError(failure.error);
            return;
        }

        if (result.sent.length === 0) {
            toastError("Failed to send the reminder. Please try again.");
            return;
        }

        markConnectionReminded(connectionId);
        toast("Reminder sent");
        onClose();
    }, [
        item.connection.id,
        remindConnections,
        markConnectionReminded,
        toast,
        toastError,
        onClose,
    ]);

    return (
        <Modal
            show={show}
            onClose={onClose}
            dialogClassName={styles.previewDialog}
        >
            <StandardModalHeader>Email client to reconnect</StandardModalHeader>
            <StandardModalBody>
                <VStack gap={16}>
                    <VStack gap={4}>
                        <span className={styles.fieldLabel}>To</span>
                        <span>{preview.recipient}</span>
                    </VStack>
                    <VStack gap={4}>
                        <span className={styles.fieldLabel}>Subject</span>
                        <span>{preview.subject}</span>
                    </VStack>
                    <VStack gap={8} className={styles.messagePreview}>
                        <span>{preview.greeting}</span>
                        <span>{preview.body}</span>
                    </VStack>
                </VStack>
            </StandardModalBody>
            <StandardModalFooter>
                <HStack gap={12} justify="end">
                    <Button variant="secondary" size="md" onClick={onClose}>
                        Cancel
                    </Button>
                    <WithHotkey
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        hotKeyOptions={{
                            description: "Send reconnect reminder",
                            preventDefault: true,
                        }}
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="primary"
                                size="md"
                                disabled={remindConnections.isPending}
                                onClick={handleSend}
                                rightIcon={hotkeyHint}
                            >
                                Send
                            </Button>
                        )}
                    </WithHotkey>
                </HStack>
            </StandardModalFooter>
        </Modal>
    );
};
